import React from 'react';
import { Input, Button, Form, Card, message } from 'antd';
import Cookies from 'js-cookie';
import umbrella from 'umbrella-storage';
import AdminService, { AdminListRequest, AdminPostParam, AdminPutParam, AdminQueryParam, IAdminInfo, IAdminInfoList } from '../../service/admin/AdminService';
import { FormInstance } from 'antd/lib/form';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import AdminRoleSelect, { AdminRole } from './AdminRoleSelect'
import StatesSelect, { States } from '../../components/public/StatesSelect';
import { ADD_ADMIN_SUCCESS_MESSAGE, ADD_ADMIN_ERROR_MESSAGE, GET_ADMIN_ERROR_MESSAGE, EDIT_ADMIN_SUCCESS_MESSAGE, EDIT_ADMIN_ERROR_MESSAGE } from '../../config/strcfg';
import './page-admin.less';
import ErrorBoundary from 'antd/lib/alert/ErrorBoundary';

interface AdminManagerPageState {
  loading: boolean;
  isEditMode: boolean;
  adminData: IAdminInfo;
  canAssignAdminRole: boolean;
  isSelfEditing: boolean;
}

class AdminManagerPage extends React.Component<AdminManagerPageState> {
  state : AdminManagerPageState = {
    loading: false,
    isEditMode: false,
    adminData: null,
    canAssignAdminRole: false,
    isSelfEditing: false,
  };

  roleParts: number[] = [1000, 100, 10, 2, 1];

  splitRoleValue = (roleValue?: number): number[] => {
    if (!roleValue || roleValue <= 0) {
      return [];
    }
    let remain = roleValue;
    const selected: number[] = [];
    this.roleParts.forEach((part) => {
      if (remain >= part) {
        remain -= part;
        selected.push(part);
      }
    });
    if (remain !== 0) {
      return [roleValue];
    }
    return selected;
  };

  sumRoleValues = (roleValues?: number[]): number => {
    return (roleValues || []).reduce((sum, current) => sum + current, 0);
  };

  async componentDidMount() {
    const loginRole = parseInt(Cookies.get('userRole') || '0', 10);
    const canAssignAdminRole = this.splitRoleValue(loginRole).includes(AdminRole.superAdmin);
    const loginAccount = umbrella.getLocalStorage('user')?.account;
    this.setState({ canAssignAdminRole });

    //根据url判断展示添加or编辑页面模式
    const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const adminId = parseInt(searchParams.get('id'));

    //编辑管理员页面模式
    if(adminId){
      this.setState({ isEditMode: true, loading: true });
      //编辑页面需要调用管理员信息查询接口
      const adminQueryParam: AdminQueryParam = { id: adminId };
      const request: AdminListRequest = {
        adminQueryParam: adminQueryParam,
        pageNo: 1,
        pageSize: 1,
      };
      try{
        const response = await AdminService.getAdminListInfo(request);
        if(response){
          const pageData: IAdminInfoList = response.data && response.data.adminInfoList ? response.data.adminInfoList : [];
          const adminRecord = pageData && pageData.records ? response.data.adminInfoList.records : null;
          if(adminRecord){
            const mappedAdminData: IAdminInfo[] = adminRecord.map((item: any) => ({
              id: item.id,
              account: item.account,
              role: item.role,
              realName: item.realName,
              avatar: item.avatar,
              states: item.states,
            }));
            const selectedAdmin = mappedAdminData[0];
            const selectedRoles = this.splitRoleValue(selectedAdmin.role);
            const isSelfEditing = selectedAdmin.account === loginAccount;
            const targetIsAdminOrAbove = selectedRoles.includes(AdminRole.commonAdmin)
              || selectedRoles.includes(AdminRole.superAdmin)
              || selectedAdmin.role >= AdminRole.commonAdmin;
            if (!canAssignAdminRole && !isSelfEditing
              && targetIsAdminOrAbove) {
              message.error('仅超级管理员可编辑管理员及超级管理员账号');
              setTimeout(() => {
                window.location.href = '/#/app/admin/list';
              }, 300);
              return;
            }
            this.setState({ adminData: selectedAdmin, loading: false, isSelfEditing }); 
            this.formRef.current?.setFieldsValue(
              { 
                account: selectedAdmin.account,
                realName: selectedAdmin.realName,
                roleValues: this.splitRoleValue(selectedAdmin.role),
                states: selectedAdmin.states,
              });
          }
          else{
            this.setState({ adminData: null });
            message.error(GET_ADMIN_ERROR_MESSAGE);
          }
        }
        else{
          this.setState({ adminData: null });
          message.error(GET_ADMIN_ERROR_MESSAGE);
        }
      } catch(error){
        message.error(GET_ADMIN_ERROR_MESSAGE);
      } finally{
        this.setState({ loading: false });
      }
    }
    //添加管理员页面模式
    else{
      this.setState({ isEditMode: false, isSelfEditing: false });
    }
  }

  formRef = React.createRef<FormInstance>();

  //权限选择框事件处理(不写这个空方法会导致页面任何操作都会使RoleSelect执行useEffect的接口调用)
  handleRoleSelectData = () => {
  };

  onFinish = async (params: any) => {
    this.setState({ loading: true });
    const selectedRoleValues: number[] = params.roleValues || [];
    const includeSuperRole = selectedRoleValues.includes(AdminRole.superAdmin);
    const includeCommonAdminRole = selectedRoleValues.includes(AdminRole.commonAdmin);
    const currentRoleValues = this.state.adminData ? this.splitRoleValue(this.state.adminData.role) : [];

    if (this.state.isSelfEditing && currentRoleValues.includes(AdminRole.commonAdmin) && !includeCommonAdminRole) {
      message.error('不能取消自身的管理员权限');
      this.setState({ loading: false });
      return;
    }
    if (this.state.isSelfEditing && currentRoleValues.includes(AdminRole.superAdmin) && !includeSuperRole) {
      message.error('不能取消自身的超级管理员权限');
      this.setState({ loading: false });
      return;
    }

    if (!this.state.canAssignAdminRole && includeSuperRole) {
      message.error('管理员不能分配超级管理员权限');
      this.setState({ loading: false });
      return;
    }
    if (!this.state.canAssignAdminRole && !this.state.isSelfEditing && includeCommonAdminRole) {
      message.error('管理员只能分配管理员以下权限');
      this.setState({ loading: false });
      return;
    }

    const roleValue = this.sumRoleValues(params.roleValues);
    const payload = {
      ...params,
      role: roleValue,
    };
    delete payload.roleValues;

    //调用addAdmin接口
    if(!this.state.isEditMode){
      try {
        const response = await AdminService.addAdmin(payload as AdminPostParam);
        if(response && response.success){
          // 添加成功后的操作
          this.formRef.current?.resetFields();
          message.success(ADD_ADMIN_SUCCESS_MESSAGE);
        }
        else{
          // 添加失败后的操作
          message.error(response.message);
        }
      } catch (error) {
          message.error(ADD_ADMIN_ERROR_MESSAGE);
      } finally {
          this.setState({ loading: false });
      }
    }
    //调用editAdmin接口
    else{
      try {
        const adminId = this.state.adminData.id;
        const updatedParams = {
          ...payload,  
          id: adminId  
        };
        const response = await AdminService.updateAdmin(updatedParams as AdminPutParam);
        if(response && response.success){
          // 编辑成功后的操作
          this.formRef.current?.resetFields();
          message.success(EDIT_ADMIN_SUCCESS_MESSAGE);
          // 在提交表单后等待500ms再刷新页面，加载提交后的数据
          setTimeout(() => { window.location.reload(); }, 500);
        }
        else{
          // 编辑失败后的操作
          this.formRef.current?.resetFields();
          message.error(response.message);
          setTimeout(() => { window.location.reload(); }, 500);
        }
      } catch (error) {
          this.formRef.current?.resetFields();
          message.error(EDIT_ADMIN_ERROR_MESSAGE);
          setTimeout(() => { window.location.reload(); }, 500);
      } finally {
          this.setState({ loading: false });
      }
    }
    
  };

  render() {
    //form 初始值
    const initialValues = {
      roleValues: this.state.adminData
        ? this.splitRoleValue(this.state.adminData.role)
        : (this.state.canAssignAdminRole ? [AdminRole.commonAdmin] : [AdminRole.sales]),
      states: this.state.adminData ? this.state.adminData.states : States.normal,
    };
    const cardTitle = this.state.isEditMode ? "编辑管理员" : "添加管理员"; 
    return (
      this.state.loading === false ?
        <ErrorBoundary>
          <div className="admin_page-container">
            <BreadcrumbCustom breads={['管理员信息管理', this.state.isEditMode ? '编辑管理员' : '添加管理员']} />
            <Card title={cardTitle} bordered={false}>
                <div className="add-admin_container">
                    <Form ref={this.formRef} onFinish={this.onFinish} initialValues={initialValues}>
                      <Form.Item label="账号名" name="account" className="formItem-admin" rules={[{ required: true, message: '请输入账号名' }]}>
                        <Input placeholder="请输入账号名" autoComplete="off" />
                      </Form.Item>

                      <Form.Item 
                        label="密码" 
                        name="password" 
                        className="formItem-admin" 
                        rules={[{ required: !this.state.isEditMode, message: '请输入密码' }]}
                      >
                        <Input.Password placeholder="请输入密码" autoComplete="new-password" />
                      </Form.Item>

                      <Form.Item label="用户名" name="realName" className="formItem-admin" rules={[{ required: true, message: '请输入用户名' }]}>
                        <Input placeholder="请输入用户名" autoComplete="off" />
                      </Form.Item>

                      <Form.Item 
                        label="权限" name="roleValues" 
                        className="formItem-admin_role" 
                        rules={[{ required: true, message: '请选择权限' }]} 
                      >
                        <AdminRoleSelect 
                          multiple
                          roleOptionProps={this.handleRoleSelectData} 
                          filterOption={(option) => {
                            if (this.state.canAssignAdminRole) {
                              return true;
                            }
                            if (this.state.isSelfEditing) {
                              return option.roleId < AdminRole.superAdmin;
                            }
                            return option.roleId < AdminRole.commonAdmin;
                          }}
                          initialValues={this.state.adminData
                            ? this.splitRoleValue(this.state.adminData.role)
                            : (this.state.canAssignAdminRole ? [AdminRole.commonAdmin] : [AdminRole.sales])}
                        />
                      </Form.Item>

                      <Form.Item 
                        label="状态" name="states"
                        className="formItem-admin_state" 
                        rules={[{ required: true, message: '请选择状态' }]}
                      >
                        <StatesSelect 
                          showAllOption={false} 
                          initialValues={this.state.adminData ? this.state.adminData.states : States.normal}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" className="button-admin_submit" htmlType="submit" loading={this.state.loading} >
                          提交
                        </Button>
                      </Form.Item>
                    </Form>
                </div>   
            </Card>
          </div> 
        </ErrorBoundary> : ""
    ); 
  }
}

export default AdminManagerPage;
